import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { ITableProps } from '../Table/models/index.js';
import { ITableRow } from '../Table/models/ITableRow.js';
import Table from '../Table/Table.js';

import Groups from './components/Groups/index.js';
import { ITableGroupingConfiguration, ITableGroupingProps, ITableGroupingState } from './models/index.js';
import { ITableGrouping } from './models/ITableGrouping.js';
import { ITableGroupingGroup } from './models/ITableGroupingGroup.js';
import { ToggleState } from './models/ToggleState.js';
import Wrapper from './styledComponents/Wrapper.js';
import { TABLE_GROUPING_CATEGORY_KEY, TABLE_GROUPING_COMPONENT_KEY } from './TableGrouping.meta.js';

export class TableGrouping extends ComponentBase<
	ITableGroupingProps,
	ITableGroupingState,
	ITableGroupingConfiguration
> {
	categoryKey = TABLE_GROUPING_CATEGORY_KEY;
	componentKey = TABLE_GROUPING_COMPONENT_KEY;

	constructor(props: ITableGroupingProps) {
		super(props);
		makeObservable(this, {
			rows: observable.ref,
			groups: observable.ref,
			componentDidUpdate: action,
			loadRows: action,
			loadGroups: action,
			mergeGroups: action,
			mergeGroup: action,
			grouping: computed,
			tableProps: computed,
			handleToggleChange: action,
			handleRowSelectionChange: action,
			handleGroupSelectionChange: action,
			updateRowsSelection: action,
		});
		this.loadRows(props.rows);
		this.groups = this.mergeGroups([], this.loadGroups(this.grouping, this.rows));
	}

	componentDidUpdate(previousProps: ITableGroupingProps) {
		if (
			common.commonUtils.stringifySafe(previousProps.rows || []) !==
			common.commonUtils.stringifySafe(this.props.rows || [])
		) {
			this.loadRows(this.props.rows);
			const newGroups = this.loadGroups(this.grouping, this.rows);
			this.groups = this.mergeGroups(this.groups, newGroups);
		}
	}

	rows: ITableRow[] = [];

	loadRows = (rows: ITableRow[] = []) => {
		this.rows = rows.map((row) => {
			if (!row._rowKey) {
				row._rowKey = common.commonUtils.generateGuid();
			}
			return row;
		});
	};

	groups: ITableGroupingGroup[] = [];

	loadGroups = (grouping: ITableGrouping | undefined, rows: ITableRow[], parentGroup?: ITableGroupingGroup) => {
		const groups: ITableGroupingGroup[] = [];
		if (grouping) {
			const { key, childGrouping } = grouping;
			const rowsByGroupingKey = rows.reduce((acc: Map<any, ITableRow[]>, row) => {
				const value = row[key];
				if (!acc.has(value)) {
					acc.set(value, []);
				}
				if (acc.has(value)) {
					const groupedRows = acc.get(value);
					if (groupedRows) {
						groupedRows.push(row);
					}
				}
				return acc;
			}, new Map());
			const values = Array.from(rowsByGroupingKey.keys());
			for (const value of values) {
				const groupedRows = rowsByGroupingKey.get(value);
				if (groupedRows) {
					const groupIndex = groups.length; // current length is the index of the new group to be pushed on.
					const group: ITableGroupingGroup = {
						value,
						groupingKey: grouping.key,
						toggleState: (grouping.toggle && grouping.toggle.defaultValue) || ToggleState.expanded,
						parent: parentGroup,
						treeIndex: `${parentGroup ? parentGroup.treeIndex + '-' : ''}${groupIndex}`,
					};
					groups.push(group);
					if (childGrouping) {
						group.children = this.loadGroups(childGrouping, groupedRows, group);
					}
				}
			}
		}
		return groups;
	};

	mergeGroups = (
		currentGroups: ITableGroupingGroup[] = [],
		newGroups: ITableGroupingGroup[] = [],
		parentGroup?: ITableGroupingGroup,
	) => {
		const response: ITableGroupingGroup[] = currentGroups;
		const currentGroupsByValue = currentGroups.reduce((acc, group) => {
			acc.set(group.value, group);
			return acc;
		}, new Map());
		for (const newGroup of newGroups) {
			const currentGroup = currentGroupsByValue.get(newGroup.value);
			if (currentGroup) {
				this.mergeGroup(currentGroup, newGroup);
			} else {
				if (parentGroup) {
					newGroup.parent = parentGroup;
				}
				response.push(newGroup);
			}
		}
		return response;
	};

	mergeGroup = (currentGroup: ITableGroupingGroup, newGroup: ITableGroupingGroup) => {
		const currentChildrenByValue = (currentGroup.children || []).reduce((acc, group) => {
			acc.set(group.value, group);
			return acc;
		}, new Map());
		const newChildren = (newGroup.children || []).filter((newChild) => !currentChildrenByValue.has(newChild.value));
		if (newChildren && newChildren.length > 0) {
			if (!currentGroup.children) {
				currentGroup.children = [];
			}
			currentGroup.children.push(
				...newChildren.map((newChild) => {
					newChild.parent = currentGroup;
					return newChild;
				}),
			);
		}
		const mergedChildren = this.mergeGroups(currentGroup.children, newGroup.children, currentGroup);
		if (mergedChildren && mergedChildren.length > 0) {
			currentGroup.children = mergedChildren;
		}
	};

	get grouping(): ITableGrouping | undefined {
		if (this.props.grouping) {
			const tableProps: ITableProps = this.props.grouping.tableProps || this.tableProps || {};
			tableProps.rowSelectionChange = this.handleRowSelectionChange;
			return {
				...this.props.grouping,
				tableProps,
				onGroupSelectionChange: this.handleGroupSelectionChange,
				toggle: {
					...(this.props.grouping.toggle || {}),
					onToggleChange: this.handleToggleChange,
				},
			};
		}
		return undefined;
	}

	get tableProps(): ITableProps {
		return {
			...(this.props.tableProps || {}),
			rows: this.rows,
		};
	}

	updateGroupTreeToggleState(rawGroups: ITableGroupingGroup[], toggleState: ToggleState, treeIndexes: number[]) {
		const groupIndex = treeIndexes.shift();
		if (groupIndex === undefined || groupIndex >= rawGroups.length) {
			return rawGroups;
		}

		const group = rawGroups[groupIndex];
		if (treeIndexes.length > 0 && group.children) {
			group.children = this.updateGroupTreeToggleState([...group.children], toggleState, treeIndexes);
			rawGroups[groupIndex] = group;
		} else {
			group.toggleState = toggleState;
		}

		return rawGroups;
	}

	handleToggleChange = (toggleState: ToggleState, treeIndex: string) => {
		const treeIndexes = treeIndex.split('-').map((i) => parseInt(i, 10));
		this.groups = this.updateGroupTreeToggleState([...this.groups], toggleState, treeIndexes);
	};

	handleRowSelectionChange = (value: boolean, row: ITableRow) => {
		this.updateRowsSelection(value, [row]);
	};

	handleGroupSelectionChange = (value: boolean, rows: ITableRow[]) => {
		this.updateRowsSelection(value, rows);
	};

	updateRowsSelection = (value: boolean, rows: ITableRow[]) => {
		const newRows = [...this.rows];
		const newRowsByRowKey = common.commonUtils.pivotCollection(newRows, (row) => row._rowKey || 'NO_ROW_KEY');
		for (const row of rows) {
			const newRow = newRowsByRowKey[row._rowKey || 'NO_ROW_KEY'];
			if (newRow) {
				newRow._selected = value;
			}
		}
		this.loadRows(newRows);
	};

	render() {
		const { className, suppressAnalytics, analyticsContext } = this.props;
		return (
			<Wrapper className={className}>
				{this.grouping && (
					<Groups
						grouping={this.grouping}
						rows={this.rows}
						groups={this.groups}
						suppressAnalytics={suppressAnalytics}
						analyticsContext={analyticsContext}
					/>
				)}
				{!this.grouping && (
					<Table
						suppressAnalytics={suppressAnalytics}
						analyticsContext={analyticsContext}
						{...this.tableProps}
					/>
				)}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(TableGrouping),
	TABLE_GROUPING_CATEGORY_KEY,
	TABLE_GROUPING_COMPONENT_KEY,
);
